import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { VIEWPORT_WIDTH, type PageEditorState } from './page-editor/usePageEditorState.js';
import { VersionSelector } from './VersionSelector.js';
import { useProjectVersions } from '../../hooks/useProjects.js';
import { type ProjectRow } from '../../hooks/useProjects.js';

interface Props {
  project: ProjectRow;
  /**
   * Estado del editor. Vive fuera porque los controles están en el panel de Evo
   * Brain, otro punto del árbol — acá solo se usa el ref del iframe y el modo.
   */
  editor: PageEditorState;
  /** Avisa qué versión se está mirando, para que el editor sepa si es la actual. */
  onVersionChange: (v: number) => void;
  /** Oculta la fila Preview/Version/abrir — la banda del paso 2 ya trae la
   *  versión en el selector de página y el historial vive en la tarjeta
   *  Versión. */
  hideHeader?: boolean;
  /** Versión controlada desde afuera (historial de la tarjeta Versión). */
  versionOverride?: number;
  /** Qué archivo de la versión mostrar. Por defecto la home. */
  file?: string;
}

export function PagePreview({ project, editor, onVersionChange, hideHeader = false, versionOverride, file = 'index.html' }: Props) {
  const { t } = useTranslation();
  const [internalVersion, setInternalVersion] = useState(project.currentVersion);
  const selectedVersion = versionOverride ?? internalVersion;
  const setSelectedVersion = setInternalVersion;
  // El lienzo es un MONITOR: alto de viewport y scroll propio. Estirar el
  // iframe al alto del documento rompe cualquier página con unidades vh
  // (un hero de 100vh pasa a medir miles de px — visto con el sitio de Evo
  // Creative). El preview de formularios (FormPreview) sí crece al alto del
  // documento; este no.


  const { data: versionsData } = useProjectVersions(project.id);
  const iframeRef = editor.iframeRef;



  // Sync selectedVersion when a new version is generated
  useEffect(() => {
    setSelectedVersion(project.currentVersion);
  }, [project.currentVersion]);



  const isViewingOld = selectedVersion < project.currentVersion;

  // Derive pending count for the drawer trigger badge.

  // Versión vieja por PATH (preview-v/N/…): los assets relativos del
  // documento heredan la versión — con ?v= el navegador los pedía sin la
  // query y salían de la versión actual (imágenes rotas al ver historial).
  // La versión actual lleva ?r=N: sin eso la URL era CONSTANTE entre
  // versiones — aplicar un comentario creaba v6 y el iframe seguía mostrando
  // v5 (misma URL ⇒ mismo key ⇒ sin remount, y el documento ya cargado no se
  // vuelve a pedir). 'r' y no 'v': el server resuelve la versión por ?v=.
  // El archivo va en el path, nunca en la query: los assets relativos del
  // documento (css/base.css, img/foto.jpg) se resuelven contra él.
  const previewUrl = isViewingOld
    ? `/api/projects/${project.id}/preview-v/${selectedVersion}/${file}`
    : `/api/projects/${project.id}/preview/${file}?r=${project.currentVersion}`;

  function reloadIframe() {
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.src = previewUrl;
    }
  }

  function handleVersionSelect(v: number) {
    // key={previewUrl} en el iframe ya fuerza el remount al cambiar la URL
    setSelectedVersion(v);
    onVersionChange(v);
  }

  return (
    // Outer shell: position:relative so DrawerSurface's position:absolute children
    // reference this container. NO padding here — padding lives in the inner wrapper
    // so the backdrop (inset:0) covers the full panel without a residual frame (IMP-4).
    <div style={{ position: 'relative' }}>
      {/* Sin padding lateral propio: el lienzo hereda el margen del tab y
          queda alineado con la banda del editor. */}
      <div style={{ padding: '32px 0', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-5)' }}>

      {/* Header row — redundante dentro del paso 2 (la banda trae la versión) */}
      {!hideHeader && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-4)', flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.preview.title')}
          </span>
          {versionsData && (
            <VersionSelector
              versions={versionsData.versions}
              currentVersion={project.currentVersion}
              selectedVersion={selectedVersion}
              onSelect={handleVersionSelect}
            />
          )}
        </div>
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-cyan)',
            fontFamily: 'var(--evo-font)',
            textDecoration: 'none',
          }}
        >
          {t('projects.preview.openInTab')}
        </a>
      </div>
      )}

      {/* Old-version banner */}
      {isViewingOld && (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          border: '1px solid var(--evo-stroke)',
          borderRadius: 'var(--evo-r-sm)',
          background: 'var(--evo-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--evo-space-4)',
        }}>
          <span style={{
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.versions.viewingOld', {
              version: selectedVersion,
              current: project.currentVersion,
            })}
          </span>
          <Button variant="link" tone="cyan" size="sm" onClick={() => handleVersionSelect(project.currentVersion)}>
            {t('projects.versions.goToCurrent')}
          </Button>
        </div>
      )}

      {/* Iframe */}
      <div style={{
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        overflow: 'hidden',
        // Centrado cuando el ancho es fijo, para que se vea como un dispositivo
        // y no pegado al borde izquierdo.
        display: 'flex',
        justifyContent: 'center',
        background: 'var(--evo-bg-2)',
      }}>
        <iframe
          key={previewUrl}
          ref={iframeRef}
          src={previewUrl}
          // El editor no puede saber solo cuándo cargó el documento: este
          // componente es el dueño del iframe, así que le avisa.
          onLoad={() => { editor.refreshStampStatus(); }}
          style={{
            width: VIEWPORT_WIDTH[editor.viewport],
            maxWidth: '100%',
            height: 'calc(100vh - var(--evo-canvas-vh-offset))',
            minHeight: 'var(--evo-canvas-min-h)',
            border: 'none',
            display: 'block',
            transition: 'width var(--evo-motion-collapse)',
          }}
          title={t('projects.preview.title')}
        />
      </div>

      {/* El drawer de imágenes pendientes se retiró de la vista
          (2026-08-30) — el botón 'todas las imágenes subidas' y su panel
          salían acá. */}
      </div>
    </div>
  );
}
